import React, { useEffect, useState } from "react";
import styles from "./anaylitcs.module.css";
import Graph from "components/Home/Graph";
import {
  FirstGraph,
  GetOrderVolume,
  horiztonalBarGraph,
  pieGraph,
} from "./testFields";
import IconButton from "common/IconButton";
import CheckBox from "common/CheckBox";
import { getAll } from "utils/queries";
import { formatDateFromDateAndTime } from "utils";
import DatePicker from "react-datepicker";

const formatMetricsData = (data, xKey, xKeysType, yKeyArray) => ({
  xAxisData: data.map((e) => {
    if (!xKeysType) {
      return formatDateFromDateAndTime(e[xKey]);
    } else {
      return e[xKey] || "none";
    }
  }),
  yAxisData: yKeyArray.map((yKey) => data.map((e) => parseInt(e[yKey], 10))),
});

const calculateInterval = (yAxisData) => {
  if (!yAxisData || yAxisData.length === 0) return 1;

  const maxValue = Math.max(...yAxisData);
  const minValue = Math.min(...yAxisData);
  const range = maxValue - 0;

  const interval = Math.floor(range / 5);
  return interval;
};

const Analytics = () => {
  const [metrics, setMetrics] = useState({});
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(false);

  const handleCheckBoxChange = (e) => {
    setIsAutoRefreshEnabled(e.target.checked);
  };

  const metricKeys = {
    avg_order_value: {
      type: "line",
      key: "avg_price",
      setter: "avgOrderValue",
      xKeys: "order_date",
      yKeys: ["avg_price"],
    },
    conversion_rate: {
      type: "line",
      key: "avg_price",
      setter: "conversionRate",
      xKeys: "date",
      yKeys: ["avg_price"],
    },
    new_vs_returning: {
      type: "line",
      key: "avg_price",
      setter: "newVsReturn",
      xKeys: "date",
      yKeys: ["new", "returning"],
    },
    orders_by_date: {
      type: "line",
      key: "avg_price",
      setter: "volumeMetrics",
      xKeys: "order_date",
      yKeys: ["order_count"],
    },
    payments_count_by_country: {
      type: "horizontal_bar",
      key: "country",
      setter: "paymentCountByCountry",
      xKeysType: "text",
      xKeys: "country",
      yKeys: ["payment_count"],
    },
    payments_count_by_payment_method: {
      type: "horizontal_bar",
      key: "avg_price",
      setter: "paymentCountByMethod",
      xKeysType: "text",
      xKeys: "payment_method",
      yKeys: ["method_count"],
    },
    repeated_customers: {
      type: "line",
      key: "avg_price",
      setter: "repeatedCustomersByDate",
      xKeys: "order_date",
      yKeys: ["user_count"],
    },
    tickets_by_date: {
      type: "line",
      key: "avg_price",
      setter: "dateByNoOfTickets",
      xKeys: "order_date",
      yKeys: ["ticket_count"],
    },
    total_revenue: {
      type: "line",
      key: "avg_price",
      setter: "revenueMetrics",
      xKeys: "order_date",
      yKeys: ["total_revenue"],
    },
    referrer_order_count: {
      type: "horizontal_bar",
      key: "avg_price",
      setter: "referrerOrderMetrics",
      xKeys: "referrer",
      yKeys: ["order_count"],
      xKeysType: "text",
    },
    referrer_count: {
      type: "pie",
      key: "avg_price",
      setter: "referrerCountMetrics",
      xKeys: "referrer",
      yKeys: ["user_count"],
      xKeysType: "text",
    },
    ticket_sales_by_event: {
      type: "pie",
      key: "avg_price",
      setter: "ticketSalesByEvent",
      xKeys: "event_name",
      yKeys: ["ticket_sales"],
      xKeysType: "text",
    },
  };

  const fetchMetricsData = async (startDate = null) => {
    try {
      const results = await getAll(`orders/metrics?date=${startDate}`);
      const data = results?.data?.data || {};

      const formattedMetrics = {};
      Object.keys(data).forEach((key) => {
        console.log("datais", data);
        if (metricKeys?.[key]) {
          const { setter, yKeys, xKeys, xKeysType } = metricKeys[key];
          const metricData = data[key] || [];

          const formattedData = formatMetricsData(
            [...metricData],
            xKeys,
            xKeysType,
            yKeys
          );
          console.log("formattedData", setter, formattedData);
          formattedMetrics[setter] = formattedData;
        }
      });

      setMetrics(formattedMetrics);
    } catch (error) {
      console.error("Error fetching metrics data:", error);
    }
  };

  console.log("metrics", metrics);

  useEffect(() => {
    fetchMetricsData(new Date());
  }, []);

  useEffect(() => {
    let interval;

    if (isAutoRefreshEnabled) {
      interval = setInterval(() => {
        console.log("Fetching data...");
        fetchMetricsData();
      }, 600000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAutoRefreshEnabled]);

  const calculateMetricSum = (metricData) => {
    return (
      metricData?.yAxisData?.[0]?.reduce(
        (acc, cur) => acc + parseFloat(cur),
        0
      ) || 0
    );
  };

  console.log("metrics", metrics);

  const renderGraphs = () => {
    const graphData = [
      {
        label: "Total Sales Volume",
        type: "line",
        data: metrics.volumeMetrics || [],
      },
      {
        label: "Total Revenue",
        type: "line",
        data: metrics.revenueMetrics || [],
      },
      {
        label: "Average Order Value",
        type: "line",
        data: metrics.avgOrderValue || [],
      },
      {
        label: "Customer Lifetime Value (CLV)",
        type: "line",
        data: metrics.repeatedCustomersByDate || [],
      },
      {
        label: "Customer Acquisition Cost (CAC)",
        type: "line",
        data: metrics.dateByNoOfTickets || [],
      },
      {
        label: "Conversion Rate",
        type: "line",
        data: metrics.conversionRate || [],
      },
      {
        label: "Traffic Sources",
        type: "pie",
        data: metrics.referrerCountMetrics || [],
        // data: pieGraph([10, 33, 25, 23]) || [],
      },
      {
        label: "Sales By Traffic",
        type: "horizontal_bar",
        data: metrics.referrerOrderMetrics || [],
      },
      {
        label: "New vs. Returning",
        type: "line",
        data: metrics.newVsReturn || [],
      },
      {
        label: "Ticket Sales by Event",
        type: "pie",
        data: metrics.ticketSalesByEvent || [],
        // data: FirstGraph([[10, 33, 25, 23, 8, 50]]) || [],
      },
      {
        label: "Payment Country",
        type: "horizontal_bar",
        data: metrics.paymentCountByCountry || [],
      },
      {
        label: "Payment Method Usage",
        type: "horizontal_bar",
        data: metrics.paymentCountByMethod || [],
      },
    ];

    return graphData?.map((graph, index) => {
      const interval = calculateInterval(graph.data?.yAxisData?.[0] || []);

      console.log("graphaindex", graph, index, interval);
      let values = () => {
        if (graph?.type == "line") {
          console.log("checkingdata", graph);
          return {
            ...GetOrderVolume({
              xAxisData: graph.data?.xAxisData,
              yAxisData: graph.data?.yAxisData,
              params: {
                y: { interval },
              },
              series: {
                smooth: false,
              },
            }),
          };
        }

        if (graph?.type == "horizontal_bar") {
          console.log("horizontal_bar", graph, metrics.paymentCountByMethod);
          return {
            ...horiztonalBarGraph({
              xAxisData: graph?.data?.xAxisData || [],
              yAxisData: graph?.data?.yAxisData || [],
              params: {
                x: { type: "value" },
                y: { interval, type: "category" },
              },
              series: {
                smooth: false,
              },
            }),
          };
        }

        if (graph?.type == "pie") {
          console.log("pie", graph, metrics.referrerCountMetrics);
          return {
            ...pieGraph({
              xAxisData: graph?.data?.xAxisData || [],
              yAxisData: graph?.data?.yAxisData?.[0] || [],
              params: {
                x: { type: "value" },
                y: { interval, type: "category" },
              },
              series: {
                smooth: false,
              },
            }),
          };
        }
      };

      return (
        <Graph
          key={index}
          label={graph.label}
          metricNumber={calculateMetricSum(graph.data)}
          {...values()}
          smooth={false}
          showHeader={true}
          showMetricNumber={true}
          type={
            graph.label.includes("Traffic")
              ? "pie"
              : graph.label.includes("Sales")
              ? "bar"
              : "line"
          }
          dashboardOverrideStyles={styles.dashboardOverrideStyles}
          grid={{
            left: "1.5%",
            right: "4%",
            bottom: "3%",
            top: "10%",
            containLabel: true,
          }}
        />
      );
    });
  };

  const [startDate, setStartDate] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const [remainingDays, setRemainingDays] = useState(7); // Default to 7 days

  const handleDateChange = (date) => {
    setStartDate(date);
    const currentDate = new Date();
    const daysDifference = Math.ceil(
      (currentDate - date) / (1000 * 60 * 60 * 24)
    );
    setRemainingDays(daysDifference);
    fetchMetricsData(date);
    console.log(`Selected range: ${daysDifference} days`);
    setIsOpen(false); // Close the calendar after selection
  };

  console.log("open is", isOpen);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.leftHeader}>
          <IconButton
            label={`Last ${remainingDays} days`}
            icon={"calendar"}
            buttonFunction={() => setIsOpen((prev) => !prev)}
          />
          {isOpen && (
            <div
              style={{
                position: "absolute",
                zIndex: 1000,
                background: "white",
                padding: "10px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                marginLeft: "125px",
                marginTop: "125px",
              }}
            >
              <DatePicker
                id="start-date"
                label="Start Date"
                selected={startDate}
                onChange={handleDateChange}
                inline
                maxDate={new Date()} // Prevent selecting future dates
                formGroupStyleOverride={{ marginBottom: "0" }}
              />
            </div>
          )}
        </div>
        <div className={styles.leftHeader}>
          <CheckBox
            label={"Auto- refresh"}
            checkBoxClassOverride={styles.checkBoxClassOverride}
            checked={isAutoRefreshEnabled}
            onChange={handleCheckBoxChange}
          />
        </div>
      </div>
      {Object.keys(metrics)?.length > 0 && (
        <div className={styles.dashboard}>{renderGraphs()}</div>
      )}
    </div>
  );
};

export default Analytics;
